import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'tw:group/badge tw:inline-flex tw:h-5 tw:w-fit tw:shrink-0 tw:items-center tw:justify-center tw:gap-1 tw:overflow-hidden tw:rounded-[10px] tw:border tw:border-transparent tw:px-2.5 tw:py-0.5 tw:text-xs tw:font-medium tw:whitespace-nowrap tw:[font-family:Inter,sans-serif] tw:transition-all tw:focus-visible:border-ring tw:focus-visible:ring-[3px] tw:focus-visible:ring-ring/50 tw:has-data-[icon=inline-end]:pr-1.5 tw:has-data-[icon=inline-start]:pl-1.5 tw:aria-invalid:border-destructive tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:ring-destructive/40 tw:[&>svg]:pointer-events-none tw:[&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'tw:bg-primary tw:text-primary-foreground',
        secondary: 'tw:bg-secondary tw:text-secondary-foreground',
        destructive: 'tw:bg-destructive tw:text-destructive-foreground',
        outline: 'tw:border-border tw:text-foreground',
        // Count/notification pill badges
        count: 'tw:bg-primary tw:text-primary-foreground tw:rounded-full',
        'count-destructive':
          'tw:bg-destructive tw:text-destructive-foreground tw:rounded-full',
        'count-outline': 'tw:border-border tw:text-foreground tw:rounded-full',
        // Semantic color badges
        amber: 'tw:bg-amber-50 tw:border-amber-300 tw:text-amber-800',
        green: 'tw:bg-green-50 tw:border-green-300 tw:text-green-800',
        orange: 'tw:bg-orange-50 tw:border-orange-300 tw:text-orange-800',
        blue: 'tw:bg-blue-50 tw:border-blue-300 tw:text-blue-800',
        pink: 'tw:bg-pink-50 tw:border-pink-300 tw:text-pink-800',
        indigo: 'tw:bg-indigo-50 tw:border-indigo-300 tw:text-indigo-800',
        cyan: 'tw:bg-cyan-50 tw:border-cyan-300 tw:text-cyan-800',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Badge({
  className,
  variant = 'default',
  render,
  ...props
}: useRender.ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ variant }), className),
      },
      props
    ),
    render,
    state: {
      slot: 'badge',
      variant,
    },
  });
}

export { Badge, badgeVariants };
